import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentClinicId } from "@/hooks/useAppointments";
import { deriveFinancialStatusWithLegacyFallback } from "@/lib/utils/appointment-financial";

export interface ProfessionalAppointmentTypeBreakdown {
  type: string;
  typeLabel: string;
  color: string | null;
  icon: string | null;
  totalAppointments: number;
  confirmados: number;
  finalizados: number;
  pagos: number;
  receita: number;
  ticketMedio: number;
  pctFinalizados: number;
  pctPagos: number;
  commission: number;
}

export interface ProfessionalItemBreakdown {
  itemId: string | null;
  itemName: string;
  itemType: "PRODUTO" | "SERVICO" | "PACOTE" | "UNKNOWN";
  quantity: number;
  appointmentCount: number;
  revenue: number;
  commission: number;
}

export interface ProfessionalPatientBreakdown {
  petId: string;
  petName: string;
  ownerId: string | null;
  ownerName: string | null;
  speciesName: string | null;
  breedName: string | null;
  appointmentCount: number;
  revenue: number;
}

export interface ProfessionalRankingRow {
  vetUserId: string | null;
  professionalName: string;
  totalAppointments: number;
  confirmados: number;
  finalizados: number;
  pagos: number;
  pctConfirmados: number;
  pctFinalizados: number;
  pctPagos: number;
  receita: number;
  commission: number;
  appointmentTypes: ProfessionalAppointmentTypeBreakdown[];
  services: ProfessionalItemBreakdown[];
  products: ProfessionalItemBreakdown[];
  patients: ProfessionalPatientBreakdown[];
}

type AppointmentFinancialSelect = { status?: string | null } | Array<{ status?: string | null }> | null | undefined;
type AppointmentItemSelect = {
  appointment_id: string;
  item_id: string | null;
  qty: number | null;
  title_snapshot: string | null;
  item?: { id?: string | null; name?: string | null; type?: string | null } | null;
};
type AppointmentPetSelect = {
  pet?: {
    id?: string | null;
    name?: string | null;
    owner_id?: string | null;
    species?: { name?: string | null } | null;
    breed?: { name?: string | null } | null;
    owner?: { id?: string | null; name?: string | null } | null;
  } | null;
};
type AppointmentVetSelect = {
  id?: string | null;
  full_name?: string | null;
} | null;
type ClinicAppointmentTypeSelect = {
  id?: string | null;
  name?: string | null;
  color?: string | null;
  icon?: string | null;
  behavior?: string | null;
} | null;
type PaidSaleRow = {
  appointment_id: string | null;
  total: number | null;
  sale_items?: Array<{
    quantity?: number | null;
    line_total?: number | null;
    item_id?: string | null;
    item_name?: string | null;
    commission_entries?: Array<{ amount: number | null }> | null;
    item?: { id?: string | null; name?: string | null; type?: string | null } | null;
  }> | null;
};

const SALES_BATCH_SIZE = 40;
const FALLBACK_APPOINTMENT_TYPE = "outro";

const sumEntries = (entries: Array<{ amount: number | null }> | null | undefined): number =>
  (entries ?? []).reduce((acc, entry) => acc + Number(entry.amount ?? 0), 0);

const extractFinancialStatus = (financial: AppointmentFinancialSelect): string | null => {
  if (!financial) return null;
  if (Array.isArray(financial)) return financial[0]?.status ?? null;
  return financial.status ?? null;
};

const fetchPaidSalesByAppointmentIds = async ({
  appointmentIds,
  clinicId,
}: {
  appointmentIds: string[];
  clinicId: string;
}): Promise<PaidSaleRow[]> => {
  const uniqueAppointmentIds = Array.from(new Set(appointmentIds)).filter(Boolean);
  if (uniqueAppointmentIds.length === 0) return [];

  const sales: PaidSaleRow[] = [];

  for (let index = 0; index < uniqueAppointmentIds.length; index += SALES_BATCH_SIZE) {
    const batchIds = uniqueAppointmentIds.slice(index, index + SALES_BATCH_SIZE);
    const { data, error } = await supabase
      .from("sales")
      .select("appointment_id, total, sale_items(quantity, line_total, item_id, item_name, commission_entries(amount), item:items(id, name, type))")
      .eq("clinic_id", clinicId)
      .eq("status", "PAGA")
      .in("appointment_id", batchIds);

    if (error) throw error;
    sales.push(...((data ?? []) as unknown as PaidSaleRow[]));
  }

  return sales;
};

export function useReportsByProfessional(
  dateFrom: Date | null,
  dateTo: Date | null,
  filters?: { type?: string; vetUserId?: string }
) {
  const { data: clinicId } = useCurrentClinicId();

  return useQuery({
    queryKey: [
      "reports-by-professional",
      clinicId,
      dateFrom?.toISOString(),
      dateTo?.toISOString(),
      filters?.type,
      filters?.vetUserId,
    ],
    queryFn: async (): Promise<ProfessionalRankingRow[]> => {
      if (!clinicId || !dateFrom || !dateTo) return [];

      const fromIso = dateFrom.toISOString();
      const toIso = dateTo.toISOString();

      let appointmentsQuery = supabase
        .from("appointments")
        .select(`
          id,
          status,
          vet_user_id,
          vet:profiles!appointments_vet_user_id_fkey(id, full_name),
          type,
          clinic_appointment_type:clinic_appointment_types(id, name, color, icon, behavior),
          appointment_financial:appointment_financials(status),
          appointment_items(
            appointment_id,
            item_id,
            qty,
            title_snapshot,
            item:items(id, name, type)
          ),
          appointment_pets(
            pet:pets(
              id,
              name,
              owner_id,
              species:species_id(name),
              breed:breed_id(name),
              owner:owners(id, name)
            )
          )
        `)
        .eq("clinic_id", clinicId)
        .gte("starts_at", fromIso)
        .lte("starts_at", toIso);

      if (filters?.type) appointmentsQuery = appointmentsQuery.eq("type", filters.type);
      if (filters?.vetUserId) appointmentsQuery = appointmentsQuery.eq("vet_user_id", filters.vetUserId);

      const { data: appointments, error: appErr } = await appointmentsQuery;

      if (appErr) throw appErr;

      const appointmentIds = (appointments ?? []).map((a) => a.id);
      const salesByAppointment: Record<string, number> = {};
      const saleItemsByAppointment: Record<string, PaidSaleRow["sale_items"]> = {};
      const commissionByAppointment: Record<string, number> = {};
      if (appointmentIds.length > 0) {
        const sales = await fetchPaidSalesByAppointmentIds({
          appointmentIds,
          clinicId,
        });
        sales.forEach((s) => {
          if (s.appointment_id) {
            salesByAppointment[s.appointment_id] = (salesByAppointment[s.appointment_id] ?? 0) + Number(s.total ?? 0);
            saleItemsByAppointment[s.appointment_id] = [
              ...(saleItemsByAppointment[s.appointment_id] ?? []),
              ...(s.sale_items ?? []),
            ];
            const appointmentCommission = (s.sale_items ?? []).reduce(
              (sum, item) => sum + sumEntries(item.commission_entries),
              0,
            );
            commissionByAppointment[s.appointment_id] =
              (commissionByAppointment[s.appointment_id] ?? 0) + appointmentCommission;
          }
        });
      }

      const vetIds = [...new Set((appointments ?? []).map((a) => a.vet_user_id).filter(Boolean))] as string[];
      const namesByVet: Record<string, string> = {};
      if (vetIds.length > 0) {
        (appointments ?? []).forEach((appointment) => {
          const vetUserId = appointment.vet_user_id;
          if (!vetUserId) return;

          const joinedVet = (appointment as unknown as { vet?: AppointmentVetSelect }).vet;
          const joinedName = joinedVet?.full_name?.trim();
          if (joinedName) namesByVet[vetUserId] = joinedName;
        });

        // Use RPC to bypass profiles RLS while still validating clinic access.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: professionals, error: profErr } = await (supabase as any).rpc(
          "get_clinic_professionals",
          { p_clinic_id: clinicId }
        );
        if (profErr) throw profErr;

        (professionals ?? []).forEach((p: { id: string; full_name: string | null }) => {
          namesByVet[p.id] = p.full_name?.trim() || namesByVet[p.id] || "Sem nome";
        });
      }

      const byVet: Record<
        string,
        {
          total: number;
          confirmados: number;
          finalizados: number;
          pagos: number;
          receita: number;
          commission: number;
          appointmentTypes: Record<string, ProfessionalAppointmentTypeBreakdown>;
          items: Record<string, ProfessionalItemBreakdown>;
          patients: Record<string, ProfessionalPatientBreakdown>;
        }
      > = {};
      const nullKey = "__null__";
      const createEmptyStats = () => ({
        total: 0,
        confirmados: 0,
        finalizados: 0,
        pagos: 0,
        receita: 0,
        commission: 0,
        appointmentTypes: {},
        items: {},
        patients: {},
      });
      byVet[nullKey] = createEmptyStats();

      (appointments ?? []).forEach((a) => {
        const key = a.vet_user_id ?? nullKey;
        if (!byVet[key]) byVet[key] = createEmptyStats();
        const operationalStatus = a.status === "pago" ? "finalizado" : a.status;
        const financialStatusRaw = extractFinancialStatus(
          (a as unknown as { appointment_financial?: AppointmentFinancialSelect }).appointment_financial,
        );
        const financialStatus = deriveFinancialStatusWithLegacyFallback(
          financialStatusRaw ? { status: financialStatusRaw } : null,
          { status: a.status ?? null, grooming_status: null },
        );
        const appointmentRevenue = salesByAppointment[a.id] ?? 0;
        const appointmentCommission = commissionByAppointment[a.id] ?? 0;
        const isConfirmed = ["confirmado", "em_atendimento", "finalizado"].includes(operationalStatus ?? "");
        const isFinished = ["finalizado"].includes(operationalStatus ?? "");
        const isPaid = financialStatus === "pago";
        const clinicAppointmentType = (a as unknown as {
          clinic_appointment_type?: ClinicAppointmentTypeSelect;
        }).clinic_appointment_type;
        const rawAppointmentType = typeof a.type === "string" && a.type.trim() ? a.type.trim() : null;
        const appointmentType = clinicAppointmentType?.id ?? rawAppointmentType ?? FALLBACK_APPOINTMENT_TYPE;
        const appointmentTypeLabel =
          clinicAppointmentType?.name?.trim() ||
          clinicAppointmentType?.behavior?.trim() ||
          rawAppointmentType ||
          FALLBACK_APPOINTMENT_TYPE;

        byVet[key].total += 1;
        if (isConfirmed) byVet[key].confirmados += 1;
        if (isFinished) byVet[key].finalizados += 1;
        if (isPaid) byVet[key].pagos += 1;
        byVet[key].receita += appointmentRevenue;
        byVet[key].commission += appointmentCommission;

        if (!byVet[key].appointmentTypes[appointmentType]) {
          byVet[key].appointmentTypes[appointmentType] = {
            type: appointmentType,
            typeLabel: appointmentTypeLabel,
            color: clinicAppointmentType?.color ?? null,
            icon: clinicAppointmentType?.icon ?? null,
            totalAppointments: 0,
            confirmados: 0,
            finalizados: 0,
            pagos: 0,
            receita: 0,
            ticketMedio: 0,
            pctFinalizados: 0,
            pctPagos: 0,
            commission: 0,
          };
        }
        const typeStats = byVet[key].appointmentTypes[appointmentType];
        typeStats.totalAppointments += 1;
        if (isConfirmed) typeStats.confirmados += 1;
        if (isFinished) typeStats.finalizados += 1;
        if (isPaid) typeStats.pagos += 1;
        typeStats.receita += appointmentRevenue;
        typeStats.commission += appointmentCommission;

        const saleItems = saleItemsByAppointment[a.id] ?? [];
        const appointmentItems = ((a as unknown as { appointment_items?: AppointmentItemSelect[] }).appointment_items ?? []);
        const itemRows = saleItems.length > 0
          ? saleItems.map((item) => ({
              itemId: item.item_id ?? item.item?.id ?? null,
              itemName: item.item?.name ?? item.item_name ?? "Item sem nome",
              itemType: item.item?.type ?? "UNKNOWN",
              quantity: Number(item.quantity ?? 0),
              revenue: Number(item.line_total ?? 0),
              commission: sumEntries(item.commission_entries),
            }))
          : appointmentItems.map((item) => ({
              itemId: item.item_id ?? item.item?.id ?? null,
              itemName: item.item?.name ?? item.title_snapshot ?? "Item sem nome",
              itemType: item.item?.type ?? "UNKNOWN",
              quantity: Number(item.qty ?? 0),
              revenue: 0,
              commission: 0,
            }));

        itemRows.forEach((item) => {
          const itemKey = item.itemId ?? item.itemName;
          if (!byVet[key].items[itemKey]) {
            byVet[key].items[itemKey] = {
              itemId: item.itemId,
              itemName: item.itemName,
              itemType: ["PRODUTO", "SERVICO", "PACOTE"].includes(item.itemType)
                ? (item.itemType as "PRODUTO" | "SERVICO" | "PACOTE")
                : "UNKNOWN",
              quantity: 0,
              appointmentCount: 0,
              revenue: 0,
              commission: 0,
            };
          }
          byVet[key].items[itemKey].quantity += item.quantity;
          byVet[key].items[itemKey].appointmentCount += 1;
          byVet[key].items[itemKey].revenue += item.revenue;
          byVet[key].items[itemKey].commission += item.commission;
        });

        const appointmentPets = ((a as unknown as { appointment_pets?: AppointmentPetSelect[] }).appointment_pets ?? [])
          .map((row) => row.pet)
          .filter((pet): pet is NonNullable<AppointmentPetSelect["pet"]> => Boolean(pet?.id));
        const revenuePerPet = appointmentPets.length > 0 ? appointmentRevenue / appointmentPets.length : 0;
        appointmentPets.forEach((pet) => {
          const petId = pet.id ?? "";
          if (!petId) return;
          if (!byVet[key].patients[petId]) {
            byVet[key].patients[petId] = {
              petId,
              petName: pet.name ?? "Pet",
              ownerId: pet.owner?.id ?? pet.owner_id ?? null,
              ownerName: pet.owner?.name ?? null,
              speciesName: pet.species?.name ?? null,
              breedName: pet.breed?.name ?? null,
              appointmentCount: 0,
              revenue: 0,
            };
          }
          byVet[key].patients[petId].appointmentCount += 1;
          byVet[key].patients[petId].revenue += revenuePerPet;
        });
      });

      return Object.entries(byVet).map(([vetUserId, stats]) => {
        const id = vetUserId === nullKey ? null : vetUserId;
        const professionalName = id ? namesByVet[id] ?? "Desconhecido" : "Sem profissional";
        const pctConfirmados = stats.total > 0 ? (stats.confirmados / stats.total) * 100 : 0;
        const pctFinalizados = stats.total > 0 ? (stats.finalizados / stats.total) * 100 : 0;
        const pctPagos = stats.total > 0 ? (stats.pagos / stats.total) * 100 : 0;
        return {
          vetUserId: id,
          professionalName,
          totalAppointments: stats.total,
          confirmados: stats.confirmados,
          finalizados: stats.finalizados,
          pagos: stats.pagos,
          pctConfirmados,
          pctFinalizados,
          pctPagos,
          receita: stats.receita,
          commission: stats.commission,
          appointmentTypes: Object.values(stats.appointmentTypes)
            .map((row) => ({
              ...row,
              ticketMedio: row.totalAppointments > 0 ? row.receita / row.totalAppointments : 0,
              pctFinalizados: row.totalAppointments > 0 ? (row.finalizados / row.totalAppointments) * 100 : 0,
              pctPagos: row.totalAppointments > 0 ? (row.pagos / row.totalAppointments) * 100 : 0,
            }))
            .sort((a, b) => b.totalAppointments - a.totalAppointments),
          services: Object.values(stats.items)
            .filter((item) => item.itemType === "SERVICO" || item.itemType === "PACOTE" || item.itemType === "UNKNOWN")
            .sort((a, b) => b.revenue - a.revenue || b.quantity - a.quantity)
            .slice(0, 8),
          products: Object.values(stats.items)
            .filter((item) => item.itemType === "PRODUTO")
            .sort((a, b) => b.revenue - a.revenue || b.quantity - a.quantity)
            .slice(0, 8),
          patients: Object.values(stats.patients)
            .sort((a, b) => b.appointmentCount - a.appointmentCount || b.revenue - a.revenue)
            .slice(0, 50),
        };
      });
    },
    enabled: !!clinicId && !!dateFrom && !!dateTo,
  });
}
